import { supabase } from './client';
import { normalizeProfile } from './utils';

export interface ProductChatItem {
  id: string;
  product_id?: string | null;
  product_catalog_id?: string | null;
  user_id: string;
  message: string;
  rating?: number | null;
  created_at: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  } | null;
}

// Create chat tied to a product (backwards compatible)
export const createProductChat = async (productId: string, userId: string, message: string, rating?: number | null) => {
  // Attempt to resolve product_catalog_id from the product record (top-level or details JSON)
  let productCatalogId: string | null = null;
  try {
    const { data: prod, error: prodErr } = await supabase
      .from('products')
      .select("product_catalog_id, details->>'product_catalog_id' as catalog_from_details, details->>'barcode' as barcode")
      .eq('id', productId)
      .single();
    if (!prodErr && prod) {
      if ((prod as any).product_catalog_id) productCatalogId = (prod as any).product_catalog_id;
      else if ((prod as any).catalog_from_details) productCatalogId = (prod as any).catalog_from_details;
      else if ((prod as any).barcode) {
        try {
          const { data: cat, error: catErr } = await supabase.from('product_catalog').select('id').eq('barcode', (prod as any).barcode).single();
          if (!catErr && cat) productCatalogId = (cat as any).id;
        } catch (_e) {
          // ignore
        }
      }
    }
  } catch (e) {
    console.warn('createProductChat: could not resolve product catalog id from products table', e);
  }

  const payload: any = { product_id: productId, user_id: userId, message };
  if (productCatalogId) payload.product_catalog_id = productCatalogId;
  if (rating !== undefined && rating !== null) payload.rating = rating;

  const { data, error } = await supabase
    .from('product_chats')
    .insert(payload)
    .select()
    .single();

  if (error) {
    console.error('Error creating product chat message:', error);
    throw error;
  }
  return data;
};

// Create chat tied to a catalog entry (preferred for shared chats across stores)
export const createCatalogProductChat = async (productCatalogId: string, userId: string, message: string, productId?: string) => {
  // For catalog products, insert into product_catalog_comments (which has RLS requiring completed order)
  const payload: any = { product_catalog_id: productCatalogId, user_id: userId, message };

  const { data, error } = await supabase
    .from('product_catalog_comments')
    .insert(payload)
    .select()
    .single();

  if (error) {
    console.error('Error creating product catalog comment:', error);
    throw error;
  }
  return data;
};

export const getProductChatsByProductId = async (productId: string): Promise<ProductChatItem[]> => {
  const { data, error } = await supabase
    .from('product_chats')
    .select('id, product_id, product_catalog_id, user_id, message, rating, created_at, profiles(username, avatar_url, display_name)')
    .eq('product_id', productId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching product chats by product id:', error);
    throw error;
  }
  return data.map((item: any) => ({
    ...item,
    profiles: normalizeProfile(item.profiles),
  }));
};

export const getProductChatsByCatalogId = async (productCatalogId: string): Promise<ProductChatItem[]> => {
  // For catalog products, read from product_catalog_comments (has RLS verified buyers only)
  const { data, error } = await supabase
    .from('product_catalog_comments')
    .select('id, product_catalog_id, user_id, message, rating, created_at, profiles(username, avatar_url, display_name)')
    .eq('product_catalog_id', productCatalogId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching product catalog comments:', error);
    throw error;
  }
  return data.map((item: any) => ({
    ...item,
    profiles: normalizeProfile(item.profiles),
  }));
};

export const getAverageRatingByProductId = async (productId: string): Promise<number | null> => {
  const { data, error } = await supabase
    .from('product_chats')
    .select('rating')
    .eq('product_id', productId)
    .not('rating', 'is', null);

  if (error) {
    console.warn('Error fetching ratings:', error);
    return null;
  }

  if (!data || data.length === 0) return null;

  const sum = data.reduce((acc, item) => acc + (item.rating || 0), 0);
  return sum / data.length;
};